import { createSelector } from 'reselect';
import { splitToLines } from '../utils/stringHelper';
import { AppState } from '~/store';
import { ByActivityRunIdState } from '../types/resources';

const getActivityRunConsole = (state: AppState) =>
  state.workspaceResources.activityRunConsoleMessages.byActivityRunId;
const getSelectedActivityRunId = (state: AppState) => state.ui.selectedActivityRunId;

export type ConsoleMessage = {
  message: string;
  timestamp: string;
  key: string;
};

export const getActivityRunConsoleMessages = createSelector(
  [getActivityRunConsole, getSelectedActivityRunId],
  (consoleEvents: ByActivityRunIdState, id: string) => {
    if (consoleEvents[id] && consoleEvents[id].bySeqNo) {
      const data: ConsoleMessage[] = Object.values(consoleEvents[id].bySeqNo)
        .map((event) => {
          const lines: string[] = splitToLines(event.message);

          return lines.map((line, index): ConsoleMessage => {
            return {
              message: `${line}${'\n'}`,
              timestamp: event.localTime,
              key: `${event.seqNo}_${index}`,
            };
          });
        })
        .reduce((result, lines) => result.concat(lines), []);

      return data;
    }

    return [];
  },
);

export const selectActivityRunIdsByProcessRunId = (state: AppState, processRunId: string) =>
  state.workspaceResources.activityRuns.idsByProcessRunId?.[processRunId] ?? [];

export const selectActivityRunCallToActionTest = createSelector(
  [
    selectActivityRunIdsByProcessRunId,
    (state: AppState) => state.workspaceResources.activityRuns.byId,
    (state, processRunId, shouldTestLimitType: boolean) => shouldTestLimitType,
  ],
  (activityRunIds, activityRunsById, shouldTestLimitType) => {
    if (!shouldTestLimitType || activityRunIds.length === 0) {
      return { reachedTimeout: false, reachedException: false };
    }
    const reachedTimeout = activityRunIds
      .slice(0, 20)
      .some((id) => activityRunsById?.[id]?.result === 'TIMEOUT');
    const reachedException = activityRunsById?.[activityRunIds[0]]?.result === 'ERR';
    return { reachedTimeout, reachedException };
  },
);
